// Детект шахматных ходов в свободном тексте (латиница + кириллица).
// Порт lib/move-detect.js из v3 (тесты едут вместе с модулем).
import { Chess, type Move } from 'chess.js';

const DEFAULT_MAX_PLIES = 6;
const LETTER_BOUNDARY_RE = /[A-Za-z0-9А-Яа-яЁё]/u;
const MOVE_LIKE_RE = /(?:[O0О]-[O0О](?:-[O0О])?[+#]?|(?:(?:Кр|[KQRBNКФСЛ])?[a-hаес]?[1-8]?[xх]?[a-hаес][1-8](?:=?[QRBNКФСЛ])?[+#]?|[A-Za-zА-Яа-яЁё]?[a-hаес]?[1-8]?[xх]?[a-zа-яё][0-9](?:=?[A-Za-zА-Яа-яЁё])?[+#]?))/giu;
const INTENT_MARKER_RE = /\b(?:if|what\s+if|play|move|go|try|consider|take)\b|(?:если|сыграть|сыгра[йею]|пойти|сходить|ход|ходом|сделать|попробовать|взять)/iu;

export interface DetectedMove {
  raw: string;
  san: string;
  key: string;
  /** Ход из «своего плана» («Nf3, потом g3»): между ним и предыдущим
   * подразумевается ответ соперника — его вставит движок в what-if. */
  planned?: boolean;
}

/** Связки «свой следующий ход», а не «альтернатива»: «потом g3», «затем Bg2». */
const PLAN_SEP_RE = /(?:потом|затем|дальше|далее|после(?:\s+этого)?|then|->|→)/iu;

export interface DetectResult {
  moves: DetectedMove[];
  invalidRaw?: string;
  /** Альтернативы ПЕРВОМУ ходу от базовой позиции («не c4, а a4»): токены,
   * которые не легли продолжением последовательности, но легальны от базы. */
  alternatives?: DetectedMove[];
}

function moveKey(mv: Move): string {
  return `${mv.from}${mv.to}${mv.promotion || ''}`;
}

function normalizeMoveToken(raw: string): string {
  let s = raw
    .trim()
    .replace(/О/g, 'O')
    .replace(/о/g, 'o')
    .replace(/0/g, 'O')
    .replace(/х/g, 'x')
    .replace(/Х/g, 'x')
    .replace(/^Кр/i, 'K')
    .replace(/^К/i, 'N')
    .replace(/^Ф/i, 'Q')
    // С — ТОЛЬКО заглавная (слон): строчная «с4» — это линия c (пешка), не Сc4
    .replace(/^С/, 'B')
    .replace(/^Л/i, 'R')
    .replace(/=К$/i, '=N')
    .replace(/=Ф$/i, '=Q')
    .replace(/=С$/i, '=B')
    .replace(/=Л$/i, '=R')
    .replace(/е/g, 'e')
    .replace(/Е/g, 'e')
    .replace(/а/g, 'a')
    .replace(/А/g, 'a')
    .replace(/с/g, 'c');
  s = s.replace(/^([a-h][18])([qrbnQRBNКФСЛ])([+#]?)$/, '$1=$2$3');
  return s
    .replace(/=К/i, '=N')
    .replace(/=Ф/i, '=Q')
    .replace(/=С/i, '=B')
    .replace(/=Л/i, '=R');
}

function isBarePawnSquare(raw: string): boolean {
  return /^[a-h][1-8]$/i.test(normalizeMoveToken(raw));
}

function hasIntentMarkerNear(text: string, index: number, length: number): boolean {
  const from = Math.max(0, index - 32);
  const to = Math.min(text.length, index + length + 32);
  return INTENT_MARKER_RE.test(text.slice(from, to));
}

/** Применить ход (SAN или UCI, обе раскладки) к позиции chess.js. */
export function applyLooseMove(chess: Chess, raw: string): Move | null {
  if (!raw || typeof raw !== 'string') return null;
  const s = normalizeMoveToken(raw);
  try {
    const m = chess.move(s);
    if (m) return m;
  } catch {
    /* не SAN — пробуем координаты */
  }
  const u = s.replace(/[\s\-x]/g, '').toLowerCase();
  const mm = u.match(/^([a-h][1-8])([a-h][1-8])([qrbn])?$/);
  if (mm) {
    try {
      const m = chess.move({ from: mm[1]!, to: mm[2]!, promotion: mm[3] || 'q' });
      if (m) return m;
    } catch {
      /* нелегально */
    }
  }
  return null;
}

export function detectMovesInText(
  text: string,
  baseFen: string,
  options: { maxPlies?: number } = {},
): DetectResult | null {
  if (!text || typeof text !== 'string') return null;
  const chess = new Chess(baseFen);
  const sequence: DetectedMove[] = [];
  const maxPlies = options.maxPlies || DEFAULT_MAX_PLIES;
  const candidates: { raw: string; index: number }[] = [];
  for (const match of text.matchAll(MOVE_LIKE_RE)) {
    const raw = match[0];
    const index = match.index ?? 0;
    const before = text[index - 1] || '';
    const after = text[index + raw.length] || '';
    if (LETTER_BOUNDARY_RE.test(before) || LETTER_BOUNDARY_RE.test(after)) continue;
    candidates.push({ raw, index });
  }
  const hasMultipleMoveTokens = candidates.length > 1;
  const alternatives: DetectedMove[] = [];
  let prevEnd = -1; // конец предыдущего принятого токена — для анализа связки
  let planMode = false;
  for (const match of candidates) {
    const raw = match.raw;
    if (isBarePawnSquare(raw) && !hasMultipleMoveTokens && !hasIntentMarkerNear(text, match.index, raw.length)) continue;
    const sep = sequence.length && prevEnd >= 0 && match.index > prevEnd ? text.slice(prevEnd, match.index) : '';
    if (planMode) {
      // после первого планового хода позиция неизвестна — токены валидирует what-if
      sequence.push({ raw, san: normalizeMoveToken(raw), key: `plan:${normalizeMoveToken(raw)}`, planned: true });
      prevEnd = match.index + raw.length;
      if (sequence.length >= maxPlies) break;
      continue;
    }
    const mv = applyLooseMove(chess, raw);
    if (mv) {
      sequence.push({ raw, san: mv.san, key: moveKey(mv) });
      prevEnd = match.index + raw.length;
      if (sequence.length >= maxPlies) break;
      continue;
    }
    // «потом X» после уже принятого хода — игрок перечисляет СВОИ ходы,
    // пропуская ответы соперника: их вставит движок (analyzeWhatIf)
    if (sequence.length && PLAN_SEP_RE.test(sep)) {
      planMode = true;
      sequence.push({ raw, san: normalizeMoveToken(raw), key: `plan:${normalizeMoveToken(raw)}`, planned: true });
      prevEnd = match.index + raw.length;
      if (sequence.length >= maxPlies) break;
      continue;
    }
    // не легло продолжением — возможно, это альтернатива первому ходу
    // («не c4, а a4», «c4 или a4»): пробуем от базовой позиции
    const altMv = sequence.length ? applyLooseMove(new Chess(baseFen), raw) : null;
    if (altMv) {
      const key = moveKey(altMv);
      const seen = sequence[0]?.key === key || alternatives.some((a) => a.key === key);
      if (!seen) alternatives.push({ raw, san: altMv.san, key });
      continue;
    }
    if (!sequence.length && /(?:x|х|[a-hаес][1-8]|O-O|0-0|О-О)/iu.test(raw)) return { moves: [], invalidRaw: raw };
    break;
  }
  if (!sequence.length) return null;
  return alternatives.length ? { moves: sequence, alternatives } : { moves: sequence };
}
